import analytics from 'modules/analytics';
import { viewer } from 'modules/analytics/constants';
import type { GetBaseViewerDataQuery } from 'generated/graphql';

type WorklistItem = NonNullable<GetBaseViewerDataQuery['worklistItems']>['items'][number];
type StudyPartial = Pick<WorklistItem['studies'][number], 'seriesCount' | 'stackedFrames'>;
type StackUnion = StudyPartial['stackedFrames'][number];
// GBVD is slim now — manual priors (GET_PRIOR_STUDY) and the hanging-protocol
// match (GET_HANGING_PROTOCOL_MATCH) are their own queries, so the GBVD refetch
// diff only covers the main case + its autoloaded priors.
export type RefetchDiffData = Pick<GetBaseViewerDataQuery, 'worklistItems'>;

const getFrameCount = (st: StackUnion): number => ('frames' in st ? (st.frames?.length ?? 0) : 0);

const getStudies = (d: Nullable<RefetchDiffData>): StudyPartial[] =>
  d?.worklistItems?.items.flatMap((i) => [...i.studies, ...(i.autoloadedPriors ?? [])]) ?? [];

export const logRefetchDiff = (
  afterData: Nullable<RefetchDiffData>,
  beforeData: Nullable<RefetchDiffData>
): void => {
  const studiesBefore = getStudies(beforeData);
  const studiesAfter = getStudies(afterData);
  const studyCountBefore = studiesBefore.length;
  const studyCountAfter = studiesAfter.length;
  const seriesBefore = studiesBefore.reduce((sum, s) => sum + s.seriesCount, 0);
  const seriesAfter = studiesAfter.reduce((sum, s) => sum + s.seriesCount, 0);
  const stacksBefore = studiesBefore.reduce((sum, s) => sum + s.stackedFrames.length, 0);
  const stacksAfter = studiesAfter.reduce((sum, s) => sum + s.stackedFrames.length, 0);
  const framesBefore = studiesBefore.reduce(
    (sum, s) => sum + s.stackedFrames.reduce((ssum, st) => ssum + getFrameCount(st), 0),
    0
  );
  const framesAfter = studiesAfter.reduce(
    (sum, s) => sum + s.stackedFrames.reduce((ssum, st) => ssum + getFrameCount(st), 0),
    0
  );

  if (
    studyCountBefore === studyCountAfter &&
    seriesBefore === seriesAfter &&
    stacksBefore === stacksAfter &&
    framesBefore === framesAfter
  ) {
    return;
  }

  analytics.track(viewer.sys.precacheRefetchDiff, {
    studyCountBefore,
    studyCountAfter,
    studyCountDiff: studyCountAfter - studyCountBefore,
    seriesCountBefore: seriesBefore,
    seriesCountAfter: seriesAfter,
    stackCountBefore: stacksBefore,
    stackCountAfter: stacksAfter,
    frameCountBefore: framesBefore,
    frameCountAfter: framesAfter,
  });
};
